import { KatAlert, KatButton, KatDropdown, KatInput, KatSpinner } from '@amzn/katal-react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SITE_ID_PARAM_NAME } from 'src/core/config/navigationRoutes';
import { useFCConfig } from 'src/features/hooks/useFCConfig';
import { useUser } from 'src/utils/UserContext';
import { updateFCConfig, triggerCategorizationGeneration } from 'src/utils/Queries';
import './FCConfigPage.css';

const FCConfigPage: React.FC = () => {
    const { user } = useUser();
    const { [SITE_ID_PARAM_NAME]: siteId } = useParams();
    const { data, isLoading, error, refetch } = useFCConfig(siteId);

    const [selectedMappingType, setSelectedMappingType] = useState<string>('');
    const [selectedGenerationType, setSelectedGenerationType] = useState<string>('');
    const [ticketUrl, setTicketUrl] = useState<string>('');
    const [saveStatus, setSaveStatus] = useState<{ success: boolean; message: string } | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isRegenerating, setIsRegenerating] = useState(false);

    // Sync dropdowns with fetched data
    useEffect(() => {
        if (data) {
            setSelectedMappingType(data.productionCapabilityMappingType || '');
            setSelectedGenerationType(data.categorizationGenerationType || '');
        }
    }, [data]);

    if (!user?.isDevAdmin) {
        return <div className="fc-config-page"><KatAlert variant="danger" header="Access Denied">Developer access required.</KatAlert></div>;
    }

    if (!siteId) {
        return <div className="fc-config-page"><KatAlert variant="info" header="No Site Selected">Provide a site ID in the URL (e.g., /fcconfig/BFI4).</KatAlert></div>;
    }

    if (isLoading) {
        return <div className="fc-config-page"><KatSpinner /></div>;
    }

    if (error) {
        return <div className="fc-config-page"><KatAlert variant="danger" header="Error">{error.message}</KatAlert></div>;
    }

    const availableMappingTypes = data?.availableProductionCapabilityMappingTypes || ['ONE_TO_ONE', 'ONE_TO_MANY', 'SINGLES_MULTIS'];
    const availableGenerationTypes = data?.availableCategorizationGenerationTypes || ['ALL_ATTRIBUTES', 'QUANTITY_BASED', 'QUANTITY_AND_PACK_TYPES_BASED'];

    const hasChanges = selectedMappingType !== (data?.productionCapabilityMappingType || '')
        || selectedGenerationType !== (data?.categorizationGenerationType || '');

    const isTicketValid = ticketUrl.trim().startsWith('https://');
    const canSave = hasChanges && isTicketValid && !isSaving;

    const handleSaveAndRegenerate = async () => {
        if (!siteId || !user) return;
        setIsSaving(true);
        setSaveStatus(null);
        try {
            const response = await updateFCConfig({
                fcName: siteId,
                productionCapabilityMappingType: selectedMappingType !== data?.productionCapabilityMappingType ? selectedMappingType : undefined,
                categorizationGenerationType: selectedGenerationType !== data?.categorizationGenerationType ? selectedGenerationType : undefined,
                ticketUrl: ticketUrl.trim(),
                updatedBy: user.login || 'unknown',
            });
            if (response?.success) {
                setSaveStatus({ success: true, message: response.message || 'Config updated and regeneration triggered.' });
                setTicketUrl('');
                refetch();
            } else {
                setSaveStatus({ success: false, message: response?.message || 'Update failed.' });
            }
        } catch (e: any) {
            setSaveStatus({ success: false, message: e.message });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fc-config-page">
            <h2>FC Configuration — {siteId}</h2>

            <div className="fc-config-card">
                <div className="fc-config-section-header">Current Configuration</div>
                <div className="fc-config-grid">
                    <div className="fc-config-item">
                        <span className="fc-config-label">FC Name</span>
                        <span className="fc-config-value">{data?.fcName ?? '-'}</span>
                    </div>
                    <div className="fc-config-item">
                        <span className="fc-config-label">PC Mapping Type</span>
                        <span className="fc-config-value">{data?.productionCapabilityMappingType ?? '-'}</span>
                    </div>
                    <div className="fc-config-item">
                        <span className="fc-config-label">Rule Generation Type</span>
                        <span className="fc-config-value">{data?.categorizationGenerationType ?? '-'}</span>
                    </div>
                </div>
                <p className="fc-config-description" style={{ marginTop: '1rem' }}>
                    Trigger categorization rule regeneration using the current config (without changing any settings).
                </p>
                <div className="fc-config-actions">
                    <KatButton
                        variant="secondary"
                        onClick={async () => {
                            if (!siteId) return;
                            setIsRegenerating(true);
                            setSaveStatus(null);
                            try {
                                const categorizationType = data?.productionCapabilityMappingType === 'ONE_DC' ? 'HOV' : 'PICKING';
                                const response = await triggerCategorizationGeneration(siteId, categorizationType);
                                setSaveStatus({ success: response?.success ?? false, message: response?.message ?? 'No response' });
                            } catch (e: any) {
                                setSaveStatus({ success: false, message: e.message });
                            } finally {
                                setIsRegenerating(false);
                            }
                        }}
                        disabled={isRegenerating || isSaving}
                        label={isRegenerating ? 'Regenerating...' : 'Regenerate Rules'}
                    />
                </div>
            </div>

            <div className="fc-config-card">
                <div className="fc-config-section-header">Update Configuration</div>
                <p className="fc-config-description">
                    Change the PC Mapping Type or Rule Generation Type for this FC. Saving will also trigger automatic categorization rule regeneration.
                </p>

                <div className="fc-config-form">
                    <div className="fc-config-form-row">
                        <label className="fc-config-form-label">PC Mapping Type</label>
                        <KatDropdown
                            value={selectedMappingType}
                            options={availableMappingTypes.map(t => ({ name: t, value: t }))}
                            onChange={(e: any) => setSelectedMappingType(e.detail?.value || '')}
                        />
                    </div>

                    <div className="fc-config-form-row">
                        <label className="fc-config-form-label">Rule Generation Type</label>
                        <KatDropdown
                            value={selectedGenerationType}
                            options={availableGenerationTypes.map(t => ({ name: t, value: t }))}
                            onChange={(e: any) => setSelectedGenerationType(e.detail?.value || '')}
                        />
                    </div>

                    <div className="fc-config-form-row">
                        <label className="fc-config-form-label">Ticket / MCM URL (required)</label>
                        <KatInput
                            value={ticketUrl}
                            placeholder="https://sim.amazon.com/issues/... or https://mcm.amazon.dev/cms/..."
                            onChange={(e: any) => setTicketUrl(e.target.value || e.detail?.value || '')}
                        />
                        {ticketUrl && !isTicketValid && (
                            <span className="fc-config-validation-error">Must be a valid HTTPS URL</span>
                        )}
                    </div>

                    <div className="fc-config-actions">
                        <KatButton
                            variant="primary"
                            onClick={handleSaveAndRegenerate}
                            disabled={!canSave}
                            label={isSaving ? 'Saving...' : 'Save & Regenerate'}
                        />
                    </div>

                    {!hasChanges && (
                        <p className="fc-config-hint">Change at least one value above to enable saving.</p>
                    )}
                </div>

                {saveStatus && (
                    <div className="fc-config-status-container">
                        <KatAlert
                            variant={saveStatus.success ? 'success' : 'danger'}
                            header={saveStatus.success ? 'Success' : 'Failed'}
                        >
                            {saveStatus.message}
                        </KatAlert>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FCConfigPage;
